import * as React from 'react';

import classNames from 'classnames';

import {colors} from '../colors/colors';
import {LoadingCircle} from '../loading-placeholder/loading-circle';
import {LoadingText} from '../loading-placeholder/loading-text';

import styles from './two-line.module.css';

type Props = {
    icon?: React.ComponentType<any>;
    iconSize?: number;
    iconColor?: string;
    avatar?: React.ReactNode;
    topRow: string | React.ReactNode;
    bottomRow?: string | React.ReactNode;
    isLoading?: boolean;
    isLarge?: boolean;
    isLargeWithSmallBottomRow?: boolean;
    isBoldTopRow?: boolean;
    lightColors?: boolean;
    shouldTruncate?: boolean;
    shouldWrap?: boolean;
    // If no second row, should the first row be centered vertically?
    shouldCenterSingleLine?: boolean;
    topRowAlignment?: 'left' | 'right';
    isDisabled?: boolean;
};

TwoLine.defaultProps = {
    iconSize: 32,
    iconColor: colors.greyLt,
    shouldTruncate: true,
};

export function TwoLine(props: Props) {
    return props.isLoading ? renderLoadingPlaceholder(props) : renderLoadedContent(props);
}

function renderLoadingPlaceholder(props: Props) {
    return (
        <div className={props.isLarge ? styles['two-line--large'] : styles['two-line']}>
            <div className={styles.icon}>
                <LoadingCircle size={32} color={colors.offWhiteDk} />
            </div>
            <div
                className={
                    props.isLarge
                        ? styles['two-line-info--large']
                        : classNames(styles['two-line-info'], 'gap-2')
                }
            >
                <div className={styles['top-row']}>
                    <LoadingText height={16} color={colors.offWhiteDk} />
                </div>
                <div className={styles['bottom-row']}>
                    <LoadingText height={16} color={colors.offWhiteMd} />
                </div>
            </div>
        </div>
    );
}

function renderLoadedContent(props: Props) {
    const twoLineStyleNames = classNames({
        [styles['two-line']]: !(props.isLarge || props.isLargeWithSmallBottomRow),
        [styles['two-line--large']]: props.isLarge || props.isLargeWithSmallBottomRow,
        [styles['two-line--light-colors']]: props.lightColors,
        [styles['two-line--disabled']]: props.isDisabled,
    });

    const topRowStyle = classNames({
        [styles['top-row']]: !props.isLargeWithSmallBottomRow,
        [styles['top-row--light']]: props.isLargeWithSmallBottomRow || !props.isBoldTopRow,
        [styles['top-row--align-center']]: props.shouldCenterSingleLine && !props.bottomRow,
        [styles['top-row--align-right']]: props.topRowAlignment === 'right',
    });

    const infoStyle = classNames({
        [styles['two-line-info']]:
            !(props.isLarge || props.isLargeWithSmallBottomRow) && !props.shouldWrap,
        [styles['two-line-info--wrap']]:
            !(props.isLarge || props.isLargeWithSmallBottomRow) && props.shouldWrap,
        [styles['two-line-info--large']]:
            (props.isLarge || props.isLargeWithSmallBottomRow) && !props.shouldWrap,
        [styles['two-line-info--large--wrap']]:
            (props.isLarge || props.isLargeWithSmallBottomRow) && props.shouldWrap,
    });

    const bottomRowStyle = classNames({
        [styles['bottom-row']]: !props.isLargeWithSmallBottomRow,
        [styles['bottom-row-small']]: props.isLargeWithSmallBottomRow,
        [styles['bottom-row--wrap']]: props.shouldWrap,
    });

    const innerRowClass =
        props.shouldTruncate && !props.shouldWrap ? styles['row-truncated'] : undefined;

    return (
        <div className={twoLineStyleNames}>
            {/* We shouldn't ever render both an icon and an avatar */}
            {props.icon &&
                renderIcon(props.icon, props.iconSize || 32, props.iconColor || colors.greyLt)}
            {props.avatar ? <div className={styles.icon}>{props.avatar}</div> : undefined}
            <div className={infoStyle}>
                <div className={topRowStyle}>
                    <div
                        title={typeof props.topRow === 'string' ? props.topRow : undefined}
                        className={props.shouldTruncate ? innerRowClass : undefined}
                    >
                        {props.topRow}
                    </div>
                </div>
                {props.bottomRow ? (
                    <div className={bottomRowStyle}>
                        <div className={props.shouldTruncate ? innerRowClass : undefined}>
                            {props.bottomRow}
                        </div>
                    </div>
                ) : undefined}
            </div>
        </div>
    );
}

function renderIcon(icon: React.ComponentType<any>, iconSize: number, iconColor: string) {
    const Icon = icon;

    return (
        <div className={styles.icon}>
            <Icon size={iconSize} wrapWithDiv={false} fill={iconColor} color={iconColor} />
        </div>
    );
}
